<script setup lang="ts">
import { debounce, sortBy } from 'lodash-es';
import { SfIconTune, useDisclosure, SfLoaderCircular, SfButton } from '@storefront-ui/vue';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger, SheetClose } from '@/components/ui/sheet'
import { whenever } from '@vueuse/core';
import VFacetFilter from '~/components/VFacetFilter.vue';
import VSortFilter from '~/components/VSortFilter.vue';
import VCategoryFilter from './VCategoryFilter.vue';

// Define props
const props = defineProps<{
  categoryId?: string; // For category pages
  searchInput?: boolean; // For search pages
  title?: string;
  description?: string;
  category?: any; // For category pages
}>();

// Composables
const { isOpen, open, close } = useDisclosure();
const { isTablet } = useBreakpoints();
const router = useRouter();
const route = useRoute();
const { query: searchQuery, data, facets, search, loading, page } = useSearch();

whenever(isTablet, close);

// State Management
const selectedSort = ref(route.query.sort?.toString() || '');
const selectedFilters = ref<Record<string, string[]>>({});

// Load search term and page from the route query
searchQuery.value = route.query.s?.toString() || '';
page.value = Number(route.query.page) || 1;

// Load selected filters from the route query
Object.entries(route.query).forEach(([key, value]) => {
    if (!['s', 'page', 'sort'].includes(key)) {
    selectedFilters.value[key] = typeof value === 'string' ? value.split(',') : [];
    }
});

// Reset filters function
function resetFilters() {
  searchQuery.value = '';
  selectedSort.value = '';
  selectedFilters.value = {};
  page.value = 1;

  router.replace({ query: {page: page.value} });
}

// Function to perform search with selected filters and sort
function performSearch() {
  search({
    facets: { ...selectedFilters.value, ...(props.categoryId ? { categories: [props.categoryId] } : {}) },
    sort: selectedSort.value,
  });
}

// Function to update query parameters in the URL
function updateQueryParams(newPage = 1) {
  const updatedQuery: Record<string, string> = { page: newPage.toString() };

  if (searchQuery.value) updatedQuery.s = searchQuery.value;
  if (selectedSort.value) updatedQuery.sort = selectedSort.value;

  Object.entries(selectedFilters.value).forEach(([key, values]) => {
    if (values.length > 0) updatedQuery[key] = values.join(',');
  });

  router.replace({ query: updatedQuery });
}

// Sort change handler
function handleSortChange() {
  page.value = 1;
  updateQueryParams(1);
  performSearch();
}

// Watch for filter changes
watch(selectedFilters, () => {
  page.value = 1;
  updateQueryParams(1);
  performSearch();
}, { deep: true });

// Watch for page changes
watch(page, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    updateQueryParams(newVal);
    performSearch();
  }
});

// Watch for changes in searchQuery (debounced for efficiency)
watch(() => searchQuery.value, debounce((newVal, oldVal) => {
  if (newVal !== oldVal) {
    page.value = 1;
    selectedFilters.value = {}; // Reset filters for a fresh search
    updateQueryParams(1);
    performSearch();
  }
}, 500));

// Perform initial search
performSearch();
</script>

<template>
  <div>
    <div v-if="title" class="mb-12">
      <h1 class="mt-10 mb-4 font-bold text-2xl border-b pb-1">{{ title }}</h1>
      <div class="prose max-w-none mb-8" v-html="description"></div>
    </div>

    <input v-if="searchInput" type="search" v-model="searchQuery" class="form-input mb-4" />

    <div v-if="!loading && data?.products.length === 0" class="my-10">
      <CategoryEmptyState>
        {{ $t('No products found') }}
      </CategoryEmptyState>
    </div>

    <div class="md:flex items-start gap-6 relative" v-else>
        <div class="hidden w-full md:w-72 md:max-w-80 px-4 md:px-0 py-2 md:static md:!block bg-white">
          <SfButton @click="resetFilters" variant="secondary" class="whitespace-nowrap px-2 py-1 md:px-4 md:py-1 !mb-4 text-xs md:text-sm font-semibold">
            <template #prefix>
              <SfIconTune />
            </template>
            {{ $t('Reset') }}
          </SfButton>
          <template v-if="category" class="mb-4">
            <VCategoryFilter :category="category" />
          </template>
          <VFacetFilter :facets="facets" v-model="selectedFilters" :loading="loading" />
        </div>

        <div class="flex-1 sticky top-0">
          <div class="relative p-2">
            <!-- <div v-if="loading" class="absolute inset-0 z-10 flex items-center justify-center rounded">
            </div> -->

            <div class="flex justify-between gap-1 items-center mb-6">
                <span class="text-xs md:text-sm">
                  <span v-if="!loading">{{ data?.pagination?.total ?? 0 }} {{ $t('Items') }}</span>
                  <!-- <SfLoaderCircular size="sm" v-else /> -->
                </span>

                <div class="flex items-center gap-1">
                    <VSortFilter v-model="selectedSort" @change="handleSortChange" class="font-semibold" />
                    <Sheet>
                        <SheetTrigger>
                            <SfButton variant="tertiary" class="md:hidden whitespace-nowrap px-2 py-1 md:px-4 md:py-2">
                                <template #prefix>
                                  <SfIconTune />
                                </template>
                                {{ $t('Filters') }}
                            </SfButton>
                        </SheetTrigger>
                        <SheetContent :side="isTablet ? 'left' : 'right'" class="flex flex-col w-full md:w-[400px]">
                            <SheetHeader>
                                <SheetTitle>Filters</SheetTitle>
                                <SheetDescription>
                                  You can select multiple filters to narrow down your search results.
                                </SheetDescription>
                            </SheetHeader>
                            <SfButton @click="resetFilters" variant="secondary" class="whitespace-nowrap px-2 py-1 md:px-4 md:py-1 text-xs md:text-sm font-semibold">
                              <template #prefix>
                                <SfIconTune />
                              </template>
                              {{ $t('Reset') }}
                            </SfButton>
                            <template v-if="category" class="mb-4">
                              <VCategoryFilter :category="category" />
                            </template>
                            <div class="flex-1 w-full overflow-y-auto">
                              <VFacetFilter :facets="facets" v-model="selectedFilters" :loading="loading" />
                            </div>
                            <div class="flex items-center justify-end">
                              <SheetClose as-child>
                                <Button class="w-full h-11">
                                  {{ $t('Show products') }}
                                </Button>
                              </SheetClose>
                            </div>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>

            <div v-if="!loading"
              class="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 lg:grid-cols-3 mb-10 md:mb-5"
              :class="{ 'opacity-50': loading }"
            >
                <VProductCard
                  v-for="(product, index) in data.products"
                  :product="product"
                  :key="`${product.id}:${index}`"
                  :data-key="`${product.id}:${index}`"
                />
            </div>
            <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 lg:grid-cols-3 mb-10 md:mb-5">
              <div v-for="i in new Array(10)" class="border border-neutral-200 rounded-md hover:shadow-lg h-full gap-2">
                <Skeleton class="w-full h-64 rounded-sm animate-pulse" />
                <div class="flex flex-col justify-between gap-2 p-4">
                  <Skeleton class="w-full h-4 rounded-sm animate-pulse" />
                  <Skeleton class="w-full h-4 rounded-sm animate-pulse" />
                </div>
              </div>
            </div>

            <PaginationSearch
              v-if="data?.pagination"
              :total="data?.pagination.total"
              :current_page="data?.pagination.current_page"
              :per_page="data?.pagination.per_page"
              :pages_count="data?.pagination.pages_count"
            />
          </div>
        </div>
    </div>
  </div>
</template>
